import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type { Resolvable } from '@/services/resolvers/types'

import { composeMessageRouteName } from '@/routes/user/compose'
import {
  reportingRoute,
  userReportingEmailLinksRouteName,
  userReportingEmailOverviewRouteName,
  userReportingEmailSendersRouteName,
  userReportingEmailSubAccountsRouteName,
  userReportingEmailTrendsRouteName,
  userReportingEmailUsersRouteName,
  userReportingSmsNumbersRouteName,
  userReportingSmsOverviewRouteName,
  userReportingSmsTrendsRouteName,
  userReportingSmsSubAccountsRouteName,
  userReportingSmsUsersRouteName,
} from '@/routes/user/reporting/reporting'

import type RouterService from '@/services/route/routerService'
import type RouterPermissionsService from '@/services/routerPermissionsService'
import type { RouteLocationRaw } from 'vue-router'
import type { MainSidebarMenuLinkItem } from '@/components/layout/pageContent/sidebar/types'
import type { Translatable } from '@/services/types'
import type { TmMenuItemBadge } from '@/components/shared/types'

export type ReportingMenuLinkItem = Omit<MainSidebarMenuLinkItem, 'badge'> & {
  isSectionActive: boolean
  badge?: Extract<TmMenuItemBadge, 'new'>
}

@injectable()
export default class ReportingRedirectResolver implements Resolvable {
  constructor(
    @inject(SERVICE_TYPES.RouterService) private readonly routerService: RouterService,
    @inject(SERVICE_TYPES.RouterPermissionsService) private readonly routerPermissionsService: RouterPermissionsService,
    @inject(SERVICE_TYPES.TranslateService) private readonly translateService: Translatable,
  ) {}

  public getReportSmsSectionRoutes() {
    return [
      {
        name: 'overview',
        route: { name: userReportingSmsOverviewRouteName },
      },
      {
        name: 'trends',
        route: { name: userReportingSmsTrendsRouteName },
      },
      {
        name: 'numbers',
        route: { name: userReportingSmsNumbersRouteName },
      },
      {
        name: 'subAccounts',
        route: { name: userReportingSmsSubAccountsRouteName },
      },
      {
        name: 'users',
        route: { name: userReportingSmsUsersRouteName },
      },
    ] as const
  }

  public getReportEmailSectionRoutes() {
    return [
      {
        name: 'overview',
        route: { name: userReportingEmailOverviewRouteName },
      },
      {
        name: 'trends',
        route: { name: userReportingEmailTrendsRouteName },
      },
      {
        name: 'users',
        route: { name: userReportingEmailUsersRouteName },
      },
      {
        name: 'sub-accounts',
        route: { name: userReportingEmailSubAccountsRouteName },
      },
      {
        name: 'senders',
        route: { name: userReportingEmailSendersRouteName },
      },
      {
        name: 'links',
        route: { name: userReportingEmailLinksRouteName },
      },
    ] as const
  }

  public getReportSectionRoutes() {
    return [
      {
        name: 'sms',
        route: { name: 'user.reporting.sms' },
        childrenRoutesList: this.getReportSmsSectionRoutes(),
      },
      {
        name: 'email',
        route: { name: 'user.reporting.email' },
        childrenRoutesList: this.getReportEmailSectionRoutes(),
      },
      {
        name: 'calls',
        route: { name: 'user.reporting.calls' },
        childrenRoutesList: undefined,
      },
      {
        name: 'auditLogs',
        route: { name: 'user.reporting.auditLogs' },
        childrenRoutesList: undefined,
      },
    ] as const
  }

  public async resolveStrategies() {
    const routes: RouteLocationRaw[] = []
    this.getReportSectionRoutes().forEach((sectionRoute) => {
      if (sectionRoute.childrenRoutesList) {
        sectionRoute.childrenRoutesList.forEach((t: (typeof sectionRoute.childrenRoutesList)[number]) => {
          routes.push(t.route)
        })
      }
      routes.push(sectionRoute.route)
    })

    await Promise.all(routes.map((t) => this.routerPermissionsService.resolveStrategiesForRoute(t)))
  }

  public getFilteredRoutes() {
    const reportingRoutes = this.getReportSectionRoutes()
    const routesList: ReportingMenuLinkItem[] = []

    const titles: Record<(typeof reportingRoutes)[number]['name'], string> = {
      sms: this.translateService.t('pageTitle.user.reporting.sms'),
      email: this.translateService.t('pageTitle.user.reporting.email'),
      calls: this.translateService.t('pageTitle.user.reporting.calls'),
      auditLogs: this.translateService.t('pageTitle.user.reporting.auditLogs'),
    }

    const badges: Partial<Record<(typeof reportingRoutes)[number]['name'], ReportingMenuLinkItem['badge']>> = {
      email: 'new',
    }

    reportingRoutes.forEach((route) => {
      let res: ReportingMenuLinkItem | null
      if (route.childrenRoutesList) {
        const firstAllowedRoute = route.childrenRoutesList.find((t: (typeof route.childrenRoutesList)[number]) => {
          return this.routerPermissionsService.isAllowAccessToRoute(t.route)
        })
        if (!firstAllowedRoute) {
          return
        }

        res = {
          itemType: 'link',
          title: titles[route.name],
          route: firstAllowedRoute.route,
          badge: badges[route.name],
          isSectionActive: this.routerService.isCurrentRouteChildOfByName(route.route),
        }
      } else if (!this.routerPermissionsService.isAllowAccessToRoute(route.route)) {
        return
      } else {
        res = {
          itemType: 'link',
          title: titles[route.name],
          route: route.route,
          badge: badges[route.name],
          isSectionActive: this.routerService.isCurrentRouteChildOfByName(route.route),
        }
      }

      routesList.push(res)
    })

    return routesList
  }

  private setFirstAvailableRoute = async () => {
    const filteredRoutes = this.getFilteredRoutes()
    const routesList = filteredRoutes.map((menuGroup) => menuGroup.route)

    const preparedRoute = routesList[0] ?? {
      name: composeMessageRouteName,
    }

    await this.routerService.replace(preparedRoute)
  }

  public async resolve() {
    if (!this.routerService.isCurrentRouteByName(reportingRoute)) {
      return
    }

    await this.resolveStrategies()
    await this.setFirstAvailableRoute()
  }
}
